import { forwardRef, useId } from 'react'
import type { SelectHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'
import { fieldBaseClass } from './Input'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string
  hint?: string
  error?: string | null
  options: SelectOption[]
  placeholder?: string
  block?: boolean
  /** Wariant filtra z rejestru spraw: „Status: w toku ▾" w jednej ramce. */
  inlineLabel?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, placeholder, block = true, inlineLabel, className, id, ...rest },
  ref,
) {
  const autoId = useId()
  const selectId = id ?? autoId

  const control = (
    <div className={cn('relative', inlineLabel && 'flex items-center')}>
      {inlineLabel ? (
        <span className="pointer-events-none absolute left-3 text-base text-muted">{inlineLabel}:</span>
      ) : null}
      <select
        ref={ref}
        id={selectId}
        className={cn(
          fieldBaseClass,
          'appearance-none pr-8 font-medium',
          error ? 'border-danger' : 'border-line',
          inlineLabel && 'pl-0 font-medium',
          className,
        )}
        style={inlineLabel ? { paddingLeft: `calc(${inlineLabel.length}ch + 1.6rem)` } : undefined}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        aria-hidden
        viewBox="0 0 10 6"
        className="pointer-events-none absolute right-3 top-1/2 h-[6px] w-[10px] -translate-y-1/2 fill-muted"
      >
        <path d="M0 0h10L5 6z" />
      </svg>
    </div>
  )

  return (
    // Wariant filtra (`inlineLabel`) musi zostać pigułką jak w mockupie rejestru spraw.
    // Natywny <select> rozciąga się do NAJDŁUŻSZEJ opcji (np. „Nova Development Sp. z o.o."),
    // więc szerokość jest tu ograniczona — wybrana wartość zostaje przycięta, nie rozpycha paska.
    <div className={cn(block ? 'w-full' : 'inline-block', inlineLabel && 'max-w-[210px]')}>
      {label ? (
        <label htmlFor={selectId} className="t-label mb-1.5 block">
          {label}
        </label>
      ) : null}
      {control}
      {error ? (
        <p className="mt-1 text-micro text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-micro text-muted">{hint}</p>
      ) : null}
    </div>
  )
})
